import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseHttpService } from '../http/base.http.service';
import { ApiRouterService } from '../http/api.router';
import { objectToSnake } from 'ts-case-convert';
import { CommunicationUserType, MessageStatus } from 'src/app/models/enums';
import { IMessage } from 'src/app/models';
import { IMessagesFilters, toAutomaticFlag } from 'src/app/filters';

@Injectable({
  providedIn: 'root',
})
export class MessageHttpService extends BaseHttpService {
  private baseURL = `messages`;

  constructor(
    @Inject(HttpClient) httpClient: HttpClient,
    private routerService: ApiRouterService,
  ) {
    super(httpClient);
  }

  public getMessages(
    type: CommunicationUserType,
    page: number = 1,
    size: number = 10
  ) {
    let path = `?page=${page}&size=${size}`;
    const url = this.routerService.mount(`${this.baseURL}/${type}/${path}`);
    return this.read(url);
  }

  public createMessage(message: IMessage, userType: CommunicationUserType) {
    let body = objectToSnake({
      client: message.client,
      subject: message.subject,
      content: message.content,
      receiverIds: message.receiverIds.map((item: any) => item.id),
      channels: message.channels.map((item: any) => item.id),
      receiver: message.receiver
    });

    const url = this.routerService.mount(`${this.baseURL}/${userType}`);
    return this.create(body, url);
  }

  public filter(
    communicationUserType: CommunicationUserType,
    page: number = 1,
    size: number = 10,
    filters: IMessagesFilters,
    searchValue?: string,
  ) {
    let path = `/filter?page=${page}&size=${size}`;
    if (filters.selectedClients.length > 0) {
      path += `&clients=${filters.selectedClients.map((client) => client.id).join(',')}`;
    }

    if (filters.selectedSenders.length > 0) {
      path += `&senders=${filters.selectedSenders.map((sender) => sender.id).join(',')}`;
    }

    if (filters.selectedReceivers.length > 0) {
      path += `&receivers=${filters.selectedReceivers.map((receiver) => receiver.id).join(',')}`;
    }

    const automatic = toAutomaticFlag(filters.selectedOrigin);
    if (automatic !== null) path += `&automatic=${automatic}`;

    if (filters.selectedEvents.length > 0) {
      path += `&events=${filters.selectedEvents.join(',')}`;
    }

    if (filters.selectedAdminAlerts) path += '&adminAlerts=true';

    path += searchValue ? `&search=${encodeURIComponent(searchValue)}` : '';
    const url = this.routerService.mount(`${this.baseURL}/${communicationUserType}${path}`);
    return this.read(url);
  }

  public getSenders(    
    userType: CommunicationUserType,
  ) {
    const url = this.routerService.mount(`${this.baseURL}/${userType}/senders`);
    return this.read(url);
  }

  public getMessageDetail(
    id: number,
    page: number = 1,
    size: number = 10,
    status: MessageStatus = MessageStatus.FAILED,
    searchValue?: string
  ) {
    let path = `?page=${page}&size=${size}&status=${status}`;
    path += searchValue ? `&search=${encodeURIComponent(searchValue)}` : '';
    const url = this.routerService.mount(`${this.baseURL}/${id}/details/${path}`);
    return this.read(url);
  }

  public getMessageReceivers(
    message: IMessage,
    userType: CommunicationUserType,
    page?: number,
    size?: number
  ) {
    let path = `${userType}/receivers?client=${message.client.id}&receiver=${message.receiver}&receiver_ids=${message.receiverIds.map((receiver) => receiver.id).join(',')}`
    if (page) path += `&page=${page}`;
    if (size) path += `&size=${size}`;
    const url = this.routerService.mount(`${this.baseURL}/${path}`);
    return this.read(url);
  }

  public getPushNotifications(page: number = 1, size: number = 20) {
    const path = `push?page=${page}&size=${size}`;
    const url = this.routerService.mount(`${this.baseURL}/${path}`);
    return this.read(url);
  }

  public getUnreadPushNotificationsCount() {
    const url = this.routerService.mount(`${this.baseURL}/push/unread-count`);
    return this.read(url);
  }

  public markPushNotificationAsRead(id: number) {
    const url = this.routerService.mount(`${this.baseURL}/push/${id}/read`);
    return this.create({}, url);
  }

  public markPushNotificationAsUnread(id: number) {
    const url = this.routerService.mount(`${this.baseURL}/push/${id}/unread`);
    return this.create({}, url);
  }

  public registerWebPushToken(token: string) {
    const url = this.routerService.mount(`${this.baseURL}/push/web-tokens`);
    return this.update({ token }, url);
  }

  public unregisterWebPushToken(token: string) {
    const url = this.routerService.mount(`${this.baseURL}/push/web-tokens`);
    return this.delete(url, { token });
  }

  public markAllPushNotificationsAsRead() {
    const url = this.routerService.mount(`${this.baseURL}/push/read-all`);
    return this.create({}, url);
  }
}
